import { useCallback, useEffect, useRef, useState } from "react";
import { deviceId } from "../lib/format.js";
import {
  isMirrorMessage, type MirrorInfo, type MirrorInput,
} from "../lib/mirror.js";

/**
 * One screen of a machine, in its own window, drawn by the window that is
 * already connected to it.
 *
 * This page holds no session and no credential. It hands its canvas to the
 * window that opened it and forwards what the operator types and clicks;
 * everything else - decoding, the connection, the password that was already
 * accepted - stays where it was. The machine sees one viewer, not two.
 */
export default function MirrorWindow({ peerId, display }: { peerId: string; display: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handedOver = useRef(false);
  const [info, setInfo] = useState<MirrorInfo | null>(null);
  const [lost, setLost] = useState(false);

  const opener = typeof window !== "undefined" ? window.opener as Window | null : null;

  const post = useCallback((msg: MirrorInput | { type: "rd-mirror-closed"; display: number }) => {
    try {
      opener?.postMessage(msg, window.location.origin);
    } catch {
      /* the other window has gone; `lost` covers the visible part */
    }
  }, [opener]);

  // --- hand the drawing surface over, once -------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || handedOver.current) return;
    if (!opener || opener.closed) { setLost(true); return; }
    handedOver.current = true;
    try {
      const offscreen = canvas.transferControlToOffscreen();
      opener.postMessage(
        { type: "rd-mirror-ready", peerId, display, canvas: offscreen },
        window.location.origin,
        [offscreen],
      );
    } catch {
      setLost(true);
    }
  }, [opener, peerId, display]);

  // --- listen for what the connected window has to say -------------------
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!isMirrorMessage(e)) return;
      if (e.data.type === "rd-mirror-info" && e.data.display === display) setInfo(e.data);
      if (e.data.type === "rd-mirror-closed" && e.data.display === display) setLost(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [display]);

  // The window that draws here needs to know when to stop.
  useEffect(() => {
    const bye = () => post({ type: "rd-mirror-closed", display });
    window.addEventListener("pagehide", bye);
    return () => {
      window.removeEventListener("pagehide", bye);
      bye();
    };
  }, [post, display]);

  // If the connected window closes, this one has nothing left to show.
  useEffect(() => {
    const timer = setInterval(() => {
      if (!opener || opener.closed) setLost(true);
    }, 2000);
    return () => clearInterval(timer);
  }, [opener]);

  // --- input, as a fraction of the canvas --------------------------------
  //
  // The two windows are different sizes and only this one knows its own, so
  // it sends where the pointer is within the picture rather than where it is
  // on this screen.
  const at = (e: MouseEvent | WheelEvent) => {
    const el = canvasRef.current;
    if (!el) return { nx: 0, ny: 0 };
    const r = el.getBoundingClientRect();
    // The picture is letterboxed inside the element; clamp so a click in the
    // black bars does not land off the far edge of the remote screen.
    const nx = Math.min(1, Math.max(0, (e.clientX - r.left) / Math.max(1, r.width)));
    const ny = Math.min(1, Math.max(0, (e.clientY - r.top) / Math.max(1, r.height)));
    return { nx, ny };
  };

  const send = (event: MirrorInput["event"]) =>
    post({ type: "rd-mirror-input", display, event });

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || lost || !info?.inputEnabled) return;

    const onMove = (e: MouseEvent) => send({ kind: "move", ...at(e) });
    const onDown = (e: MouseEvent) => { e.preventDefault(); send({ kind: "down", ...at(e), button: e.button }); };
    const onUp = (e: MouseEvent) => { e.preventDefault(); send({ kind: "up", ...at(e), button: e.button }); };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      send({
        kind: "wheel", ...at(e), dx: e.deltaX, dy: e.deltaY,
        // Passed on rather than interpreted: the window holding the
        // connection decides what a trackpad means, so both windows scroll
        // and pinch the same way.
        pixels: e.deltaMode === 0, ctrl: e.ctrlKey,
      });
    };
    const onContext = (e: Event) => e.preventDefault();
    const onKey = (e: KeyboardEvent) => {
      // The browser's own shortcuts stay with the browser; everything else
      // belongs to the machine.
      if (e.metaKey && ["KeyW", "KeyT", "KeyN", "KeyQ"].includes(e.code)) return;
      e.preventDefault();
      send({
        kind: "key", down: e.type === "keydown", code: e.code, key: e.key,
        ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey, meta: e.metaKey,
      });
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("contextmenu", onContext);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("contextmenu", onContext);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lost, info?.inputEnabled, display]);

  return (
    <div className="session">
      <div className="session__bar">
        <strong style={{ fontSize: 13 }}>
          {info?.hostname || deviceId(peerId)}
        </strong>
        <span className="small faint">Screen {display + 1}</span>
        {info?.width ? (
          <span className="session__stat">{info.width}&times;{info.height}</span>
        ) : null}
        <div className="spacer" />
        {lost ? null : (
          <span className="small faint">
            {info?.inputEnabled ? "Viewing + control" : "View only"}
          </span>
        )}
        <button className="btn btn--ghost btn--sm" onClick={() => window.close()}>Close</button>
      </div>

      <div className="session__body">
        <div className="session__stage">
          <canvas ref={canvasRef} className="session__canvas session__canvas--fit" />
          {lost ? (
            <div className="session__status">
              <div className="empty">
                <h3>This window is on its own</h3>
                <p style={{ margin: "0 auto 1rem", maxWidth: "44ch" }}>
                  It was drawing a screen from the window that is connected to{" "}
                  {info?.hostname || deviceId(peerId)}, and that window has gone.
                  Open the machine again from the console to carry on.
                </p>
                <button className="btn btn--primary" onClick={() => window.close()}>Close</button>
              </div>
            </div>
          ) : !info ? (
            <div className="session__status"><span className="faint">Waiting for the picture&hellip;</span></div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
